import React, { useState, useRef, useEffect, ComponentPropsWithoutRef } from 'react';
import SRPFilters from '@components/SearchExperiment/SRPFilters/SRPFilters';
import useIsMobile from '@hooks/useIsMobile';
import useRect from '@hooks/useRect';

export default function DesktopSRPFilters(props: ComponentPropsWithoutRef<typeof SRPFilters>) {
  const isMobile = useIsMobile({ isSolarSystem: true });
  const parentRef = useRef<HTMLDivElement>(null);
  const [ref, rect] = useRect<HTMLDivElement>(['resize', 'scroll'], { passive: true });
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    if (!rect || !parentRef.current) return;
    parentRef.current.style.height = 'auto';
    parentRef.current.style.position = 'inherit';
    parentRef.current.style.top = 'inherit';

    const headerBoundingBox = document.getElementById('desktop-header')?.getBoundingClientRect();

    // Get the height of the header to determine the remaining height
    const offset = headerBoundingBox?.height ?? 136;
    const effectiveHeight = window.innerHeight - offset;

    // 40px offset is to compensate for the sticky banner
    parentRef.current.style.height = `${effectiveHeight}px`;
    parentRef.current.style.position = 'sticky';
    parentRef.current.style.top = `${offset}px`;
    parentRef.current.scrollTop = scrollY;
  }, [ref, rect, scrollY]);

  // This effect is used to save the scroll position of the nav pane.
  // This is used to restore the scroll state during any rerenders.
  useEffect(() => {
    const scrollHandler = () => {
      if (!parentRef.current) return;
      setScrollY(parentRef.current.scrollTop);
    };

    if (parentRef.current) {
      parentRef.current.addEventListener('scroll', scrollHandler, { passive: true });
    }

    // save the parent for cleanup
    const parent = parentRef.current;
    return () => {
      if (parent) {
        parent.removeEventListener('scroll', scrollHandler);
      }
    };
  }, [parentRef]);

  return (
    <aside className="hidden desktop:block w-330 flex-none relative pb-spacing-lg mr-spacing-lg">
      <div ref={parentRef} className="hover-scroll-y py-40px pr-spacing-lg">
        <div ref={ref} className="-my-spacing-md">
          {!isMobile && <SRPFilters {...props} />}
        </div>
      </div>
    </aside>
  );
}
